import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import "../compnay/ManageCompany.css";

const VIOLATION_TYPES = [
  { value: "", label: "All types" },
  { value: "LateDelivery", label: "Late Delivery" },
  { value: "OnTimeDelivery", label: "On-Time Delivery" },
  { value: "MissedAssignedOrder", label: "Missed Assigned Order" },
  { value: "OrderRejected", label: "Order Rejected" },
  { value: "OrderNotPickedUp", label: "Order Not Picked Up" },
  { value: "OrderCancelledByRider", label: "Order Cancelled by Rider" },
  { value: "CustomerComplaint", label: "Customer Complaint" },
  { value: "Other", label: "Other" },
];

const ManageRiderViolationHistory = () => {
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [violationType, setViolationType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
      height: "44px",
      width: "100%",
    }),
    [isDarkMode]
  );

  const fetchViolations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RIDER_VIOLATIONS, {
        params: {
          violationType: violationType || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          pageNumber,
          pageSize,
        },
      });
      if (response.data.status) {
        const data = response.data.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotalRecords(data.totalRecords || 0);
      } else {
        showErrorToast(response.data.message || "Failed to load violations");
      }
    } catch {
      showErrorToast("Failed to load violation history");
    } finally {
      setLoading(false);
    }
  }, [violationType, fromDate, toDate, pageNumber, pageSize]);

  useEffect(() => {
    fetchViolations();
  }, [fetchViolations]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Rider Violation History</li>
          </ol>
          <h1 className="page-header mb-0">Rider Violation History</h1>
          <p className="mb-0 mt-2" style={{ maxWidth: 720, opacity: 0.75 }}>
            Audit trail of auto-detected events. Action stays Recorded until a rule threshold fires,
            then shows Warning / Star Deducted / Star Awarded.
          </p>
        </div>
      </div>

      <div className="card border-0 mb-3">
        <div className="p-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Violation type</label>
              <select
                style={fieldStyle}
                value={violationType}
                onChange={(e) => {
                  setPageNumber(1);
                  setViolationType(e.target.value);
                }}
              >
                {VIOLATION_TYPES.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <FormInput
                label="From date"
                type="date"
                value={fromDate}
                onchange={(e) => {
                  setPageNumber(1);
                  setFromDate(e.target.value);
                }}
              />
            </div>
            <div className="col-md-3">
              <FormInput
                label="To date"
                type="date"
                value={toDate}
                onchange={(e) => {
                  setPageNumber(1);
                  setToDate(e.target.value);
                }}
              />
            </div>
            <div className="col-md-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setViolationType("");
                  setFromDate("");
                  setToDate("");
                  setPageNumber(1);
                }}
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Rider</th>
                  <th>Order</th>
                  <th>Violation</th>
                  <th>Delay</th>
                  <th>Action</th>
                  <th>Rule</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No violation history found.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr key={row.violation_ID ?? row.Violation_ID}>
                      <td>{formatDate(row.occurredAt ?? row.OccurredAt)}</td>
                      <td>{row.riderName ?? row.RiderName ?? "—"}</td>
                      <td>{row.recvorder_InvoiceNo ?? row.Recvorder_InvoiceNo ?? "—"}</td>
                      <td>{row.violationType ?? row.ViolationType}</td>
                      <td>
                        {(row.delayMinutes ?? row.DelayMinutes) != null
                          ? `${row.delayMinutes ?? row.DelayMinutes} min`
                          : "—"}
                      </td>
                      <td>{row.actionTaken ?? row.ActionTaken}</td>
                      <td>{row.ruleName ?? row.RuleName ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalRecords > pageSize && (
            <Pagination
              currentPage={pageNumber}
              pageNumber={pageNumber}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              setPageNumber={setPageNumber}
              onPageChange={setPageNumber}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageRiderViolationHistory;
